import { useEffect, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { api, type AdminIdentity } from './api';
import { configError, loginRequest } from './auth';
import { Notice, describeError } from './components/ui';
import { Changes } from './pages/Changes';
import { Contacts } from './pages/Contacts';
import { Dashboard } from './pages/Dashboard';
import { Access } from './pages/Access';
import { Settings } from './pages/Settings';

const TABS = [
  { id: 'dashboard', label: 'Overzicht', render: () => <Dashboard /> },
  { id: 'contacts', label: 'Contacten', render: () => <Contacts /> },
  { id: 'changes', label: 'Wijzigingen', render: () => <Changes /> },
  { id: 'access', label: 'Gebruikerstoegang', render: () => <Access /> },
  { id: 'settings', label: 'Instellingen', render: () => <Settings /> },
] as const;

type TabId = (typeof TABS)[number]['id'];

function tabFromHash(): TabId {
  const hash = window.location.hash.replace('#', '');
  return TABS.some((tab) => tab.id === hash) ? (hash as TabId) : 'dashboard';
}

export function App() {
  if (configError) {
    return (
      <div className="center-screen">
        <div className="login">
          <h1>Portaal niet geconfigureerd</h1>
          <p>{configError}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <Portal />
      </AuthenticatedTemplate>
    </>
  );
}

function LoginScreen() {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState<string | null>(null);

  const busy = inProgress !== InteractionStatus.None;

  return (
    <div className="center-screen">
      <div className="login">
        <h1>CardDAV-beheer</h1>
        <p>Meld je aan met je werkaccount om het adresboek te beheren.</p>

        {error ? (
          <div style={{ textAlign: 'left', marginBottom: 16 }}>
            <Notice kind="danger" title="Inloggen mislukte">
              {error}
            </Notice>
          </div>
        ) : null}

        <button
          type="button"
          className="btn primary"
          disabled={busy}
          onClick={() => {
            setError(null);
            instance.loginRedirect(loginRequest).catch((cause: unknown) => {
              setError(describeError(cause));
            });
          }}
        >
          {busy ? 'Bezig…' : 'Aanmelden met Microsoft'}
        </button>
      </div>
    </div>
  );
}

function Portal() {
  const { instance } = useMsal();
  const [tab, setTab] = useState<TabId>(tabFromHash);
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    api.me().then(setIdentity, (cause: unknown) => setAccessError(describeError(cause)));
  }, []);

  const active = TABS.find((entry) => entry.id === tab) ?? TABS[0];

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          CardDAV<span>beheerportaal</span>
        </div>

        <nav className="tabs" aria-label="Onderdelen">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="tab"
              aria-current={entry.id === tab ? 'page' : undefined}
              onClick={() => {
                window.location.hash = entry.id;
                setTab(entry.id);
              }}
            >
              {entry.label}
            </button>
          ))}
        </nav>

        <div className="identity">
          <span>{identity?.name ?? identity?.upn ?? '…'}</span>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void instance.logoutRedirect();
            }}
          >
            Afmelden
          </button>
        </div>
      </header>

      <main>
        {accessError ? (
          <Notice kind="danger" title="Geen toegang tot de beheer-API">
            {accessError}
          </Notice>
        ) : (
          active.render()
        )}
      </main>
    </div>
  );
}
